import { getTrainingFee, SECONDARY_TERM_FEE, PROFESSIONAL_COURSE_FEES, STUDENT_TYPES } from '../services/studentService';

function resolveCourseForFee(student) {
  const raw = (student?.course || student?.fieldOfStudy || '').trim();
  if (!raw) return undefined;
  if (PROFESSIONAL_COURSE_FEES[raw] != null) return raw;
  const upper = raw.toUpperCase();
  const key = Object.keys(PROFESSIONAL_COURSE_FEES).find(
    (k) => k === upper || upper.includes(k) || k.includes(upper)
  );
  return key || raw;
}

function computePaidTotal(payments, totalDue) {
  const amountSum = payments.reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
  if (amountSum > 0) return amountSum;

  const hasLegacy = payments.some(
    (p) => (!p.amount || p.amount === 0) && (p.receiptPath || p.paymentProof)
  );
  return hasLegacy ? totalDue : 0;
}

function getPaymentStatus(totalDue, totalPaid) {
  if (totalDue <= 0) return totalPaid > 0 ? 'paid' : 'unpaid';
  if (totalPaid >= totalDue) return 'paid';
  if (totalPaid > 0) return 'partial';
  return 'unpaid';
}

export function getStudentTotalDue(student) {
  const course = resolveCourseForFee(student);
  const fee = getTrainingFee(student.studentType, course);
  if (fee.amount != null) return fee.amount;
  if (student?.studentType === STUDENT_TYPES.PROFESSIONAL) return 0;
  return SECONDARY_TERM_FEE;
}

export function computePaymentSummary(student, payments) {
  const studentId = String(student._id);
  const studentPayments = payments.filter(
    (p) => String(p.student?._id || p.student) === studentId
  );
  const totalDue = getStudentTotalDue(student);
  const totalPaid = computePaidTotal(studentPayments, totalDue);
  const balance = Math.max(0, totalDue - totalPaid);

  return {
    studentId,
    totalDue,
    totalPaid,
    balance,
    status: getPaymentStatus(totalDue, totalPaid),
    currency: 'RWF',
    paymentCount: studentPayments.length,
    studentType: student.studentType,
  };
}

export function computeAllBalances(students, payments) {
  return students.map((s) => computePaymentSummary(s, payments));
}

/** Aggregate payment stats for a cohort (batch-filtered student balance rows + payments). */
export function computeBatchPaymentStats(balanceRows, batchPayments) {
  const stats = {
    students: balanceRows.length,
    paid: 0,
    partial: 0,
    unpaid: 0,
    totalDue: 0,
    totalPaid: 0,
    outstanding: 0,
    txCount: batchPayments.length,
    txTotal: 0,
  };

  balanceRows.forEach(({ balance }) => {
    stats.totalDue += balance.totalDue || 0;
    stats.totalPaid += balance.totalPaid || 0;
    stats.outstanding += balance.balance > 0 ? balance.balance : 0;
    if (balance.status === 'paid') stats.paid += 1;
    else if (balance.status === 'partial') stats.partial += 1;
    else stats.unpaid += 1;
  });

  batchPayments.forEach((p) => {
    stats.txTotal += Number(p.amount) || 0;
  });

  stats.collectionRate = stats.totalDue > 0
    ? Math.min(100, Math.round((stats.totalPaid / stats.totalDue) * 100))
    : 0;

  return stats;
}
